import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
    ClosingWizardCurrencyOption,
    ClosingWizardErp,
    ClosingWizardStep,
    ClosingWizardStepKey,
    ClosingWizardYear,
} from './closing-setup-wizard.types';

const NOT_FOUND = -1;
const FIRST = 0;
const ONE = 1;

/**
 * The Setup-Wizard — a pure view (REV-21354).
 *
 * It walks the trustee from "this closing has not started" to "start it": where the accounting data
 * comes from and where the documents live. The last screen starts the closing; the import then runs
 * in the background while the overview shows its progress.
 *
 * ⚠️ The DOCUMENTS step is PROJECTED, not rendered here. Its content is
 * `aumico-integration-sharepoint-folders-container` (REV-21057 / REV-21679), a smart container that
 * owns the Microsoft consent, the file picker and the folder bindings. Re-implementing its tiles here
 * would be a second folder picker to keep in step with the first; projecting it keeps this view dumb
 * AND keeps that flow in the one place that already ships it.
 *
 * The ACCOUNTING step is read-only: it shows the company's connected ERP and the years the start
 * loads, or — without an ERP — the way into the settings. Nothing is connected or uploaded here.
 */
@Component({
    selector: 'abcp-closing-setup-wizard',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './closing-setup-wizard.component.html',
    styleUrls: ['./closing-setup-wizard.component.scss'],
})
export class ClosingSetupWizardComponent {
    /** The screens this closing actually needs, in order. */
    @Input() steps: readonly ClosingWizardStep[] = [];

    @Input() activeStep: ClosingWizardStepKey = 'DOCUMENTS';

    // --- accounting step -------------------------------------------------------------------------

    /** The company's connected ERP, or `null` when it has none — then the step offers the settings. */
    @Input() erp: ClosingWizardErp | null = null;

    /** The closing year and the previous year, as the start will load them. */
    @Input() years: readonly ClosingWizardYear[] = [];

    /** The currencies to choose from, and the chosen one — every amount of the closing is in it. */
    @Input() currencyOptions: readonly ClosingWizardCurrencyOption[] = [];
    @Input() currency: string | null = null;

    /**
     * Whether the current step is answered well enough to move on.
     *
     * Decided by the CONTAINER and not here: "answered" depends on state this view never sees — a
     * live connection, a persisted folder binding — and a second rule in the view would let the
     * button and the server disagree about what is complete.
     */
    @Input() canAdvance = false;

    /** True between pressing start and the server acknowledging it. Disables the button. */
    @Input() starting = false;

    /**
     * Whether this closing has already been launched once.
     *
     * Drives the warning on the last step: re-running the wizard on a started closing does not
     * merely resume — if the answers changed it restarts the whole launch and discards the AI run
     * already under way (Code-Review Runde 2, R2-03). The trustee should learn that before pressing
     * the button, not from the manual afterwards.
     */
    @Input() alreadyStarted = false;

    /** «Verwaltet in …» on the ERP tile — the container knows where that is. */
    @Output() erpManageRequested = new EventEmitter<void>();
    /** «In den Einstellungen einrichten» when no ERP is connected. */
    @Output() erpSetupRequested = new EventEmitter<void>();
    /** The trustee picked (or cleared) the closing's currency. The container keeps and saves it. */
    @Output() currencyChange = new EventEmitter<string | null>();
    @Output() advanced = new EventEmitter<void>();
    @Output() wentBack = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();
    @Output() started = new EventEmitter<void>();

    onCurrencyChange(currency: string | null): void {
        this.currencyChange.emit(currency);
    }

    get isAccountingStep(): boolean {
        return this.activeStep === 'ACCOUNTING';
    }

    get isDocumentsStep(): boolean {
        return this.activeStep === 'DOCUMENTS';
    }

    /** The last screen starts the closing instead of moving on. */
    get isLastStep(): boolean {
        return this.activeIndex === this.steps.length - ONE;
    }

    /** Whether there is a screen before this one — drives "Zurück" rather than "Abbrechen". */
    get hasPrevious(): boolean {
        return this.activeIndex > FIRST;
    }

    public trackByLine(index: number): number {
        return index;
    }

    /**
     * Position of the active step, or `0` when it is not in the list.
     *
     * `0` rather than `-1`: a step the container has not declared is a bug, and falling back to "the
     * first one" leaves a usable screen instead of a stepper with nothing marked.
     */
    private get activeIndex(): number {
        const index = this.steps.findIndex((step) => step.key === this.activeStep);

        return index === NOT_FOUND ? FIRST : index;
    }

    /** A step the trustee has already passed — ticked rather than numbered. */
    public isComplete(step: ClosingWizardStep): boolean {
        return this.steps.indexOf(step) < this.activeIndex;
    }

    public isActive(step: ClosingWizardStep): boolean {
        return step.key === this.activeStep;
    }

    /** One-based, because the stepper shows it to a human. */
    public displayNumber(step: ClosingWizardStep): number {
        return this.steps.indexOf(step) + ONE;
    }

    public trackByKey(_index: number, step: ClosingWizardStep): string {
        return step.key;
    }

    public onManageErp(): void {
        this.erpManageRequested.emit();
    }

    public onSetupErp(): void {
        this.erpSetupRequested.emit();
    }

    public onNext(): void {
        this.advanced.emit();
    }

    public onBack(): void {
        this.wentBack.emit();
    }

    public onCancel(): void {
        this.cancelled.emit();
    }

    public onStart(): void {
        this.started.emit();
    }
}
